import { Tabs, Tab } from '@lmig/lmds-react-tabs';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { GridCol, GridRow } from '@lmig/lmds-react-grid';
import { Button } from '@lmig/lmds-react-button';
import { FieldGroup } from '@lmig/lmds-react-field-group';
import { Form } from '@lmig/lmds-react-form';
import { useEffect, useState } from 'react';
import RecentActivitiesTable from './RecentActivitiesTable';
import ExportRecentActivitiesUtility from 'Utilities/ExportRecentActivitiesUtility';
import SortTableDataUtility from 'Utilities/SortTableDataUtility';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { billingConfig, claimsConfig, policyConfig } from './RecentActivitiesTabConfig';
import { getBLAgentPortalLink } from '../../../Foundation/Configurations/GlobalConfiguration';
import { useUserData } from 'Foundation/User/UserContext';

interface IRecentActivitiesProps {
  sitecorelink?: ISitecoreLink;
}
const RecentActivities = ({ sitecorelink }: IRecentActivitiesProps) => {
  const [activities, setActivities] = useState<{ [key: string]: { tableData?: any[]; tabCount?: number } }>({
    billing: {},
    claims: {},
    policy: {},
  });
  const config: { [key: string]: any } = {
    billing: {
      sortedColumn: billingConfig.defaultSortedColumn,
      columns: billingConfig.columns,
    },
    claims: {
      sortedColumn: claimsConfig.defaultSortedColumn,
      columns: claimsConfig.columns,
    },
    policy: {
      sortedColumn: policyConfig.defaultSortedColumn,
      columns: policyConfig.columns,
    },
  };
  const exportUtility: any = ExportRecentActivitiesUtility.getInstance();
  const sortUtility: any = SortTableDataUtility.getInstance();
  const policyLink = getBLAgentPortalLink();
  const [agencyCode, setAgencyCode] = useState<string>('');
  const [usedAgencyCode, setUsedAgencyCode] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [isValidAgency, setIsValidAgency] = useState<boolean>(true);
  const [runReport, setRunReport] = useState<boolean>(false);
  const { userData } = useUserData();

  const isLoaded = () => {
    if (!userData) return false;
    if (
      ((!userData.isAgent() && runReport === true) || userData.isAgent()) &&
      activities['billing'].tabCount !== undefined &&
      activities['policy'].tabCount !== undefined &&
      activities['claims'].tabCount !== undefined
    )
      return true;
    else return false;
  };

  const handleTableData = (data: any, activityDataType: string) => {
    setActivities((prevState) => ({ ...prevState, [activityDataType]: { tableData: data, tabCount: data.length } }));
  };

  useEffect(() => {
    //Return functions in useEffect execute on unmount process.
    //Exists to handle memory leak messages, cleans state on unmount.
    return () => {
      setActivities({});
    };
  }, []);

  const exportRecentActivities = () => {
    // exportUtility !== null && exportUtility !== never
    // ? exportUtility.ExportData(activities["billing"].tableData, activities["policy"].tableData, activities["claims"].tableData)
    // : undefined
    if (exportUtility) {
      exportUtility.ExportData(
        activities['billing'].tableData,
        activities['policy'].tableData,
        activities['claims'].tableData,
      );
    }
  };

  const getSortedItems = (columnToSort: any, activityDataType: string, result: any, shouldToggle: boolean) => {
    let tempRows = result ? result : activities[activityDataType].tableData;
    if (shouldToggle) toggleSortedColumn(columnToSort, activityDataType);

    let tempSortedCol = config[activityDataType].sortedColumn;
    let newRows = sortUtility.getSortedItems(tempSortedCol, tempRows);

    return newRows;
  };

  const toggleSortedColumn = (columnToSort: any, activityDataType: string) => {
    sortUtility.toggleSortedColumn(columnToSort, config[activityDataType], faSortUp, faSortDown);
  };

  const validateAgencyCodeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let inputText = e?.target.value;
    let isnum = /^\d+$/.test(inputText);
    if (isnum || inputText.length === 0) {
      setAgencyCode(inputText);
    }
    if ((isnum && (inputText.length === 6 || inputText.length === 7)) || inputText.length === 0) {
      setIsValidAgency(true);
      setError(false);
    } else setIsValidAgency(false);
  };

  const validateAndSubmit = () => {
    if (!isValidAgency) setError(true);
    else if (!error && agencyCode !== usedAgencyCode && (agencyCode.length === 6 || agencyCode.length === 7)) {
      if (runReport) {
        setRunReport(false);
      }
      setUsedAgencyCode(agencyCode);
    }
  };

  useEffect(() => {
    if (!userData) return;
    if (
      !userData.isAgent() &&
      !error &&
      agencyCode === usedAgencyCode &&
      (agencyCode.length === 6 || agencyCode.length === 7)
    ) {
      setRunReport(true);
    }
    // eslint-disable-next-line
  }, [usedAgencyCode]);

  return (
    <>
      {userData && (
        <div>
          <div>
            {!userData.isAgent() && !userData.isMockingAgent ? (
              <Form id="recent-activities-form" onSubmit={() => validateAndSubmit()}>
                <GridRow justify="center">
                  <GridCol>
                    <FieldGroup
                      id="recent-activities-fieldgroup"
                      labelVisual="Enter an agency code to search"
                      messages={
                        error
                          ? [
                              {
                                text: 'You have entered an invalid agency format. Please check the number and try again.',
                                type: 'error',
                              },
                            ]
                          : undefined
                      }
                      // isFull
                    >
                      <AlphanumericInput
                        id="agency-code"
                        highlightType={error ? 'error' : undefined}
                        labelVisual="Agency code"
                        value={agencyCode}
                        autoFocus
                        // error=""
                        minLength={6}
                        maxLength={7}
                        aria-label="agency-code-input"
                        onChange={(event: any) => validateAgencyCodeInput(event)}
                      />
                      <Button
                        id="recent-activities-button"
                        className="recent-activities-buttons"
                        dynamicWidth
                        variant="primary"
                        size="small"
                        onClick={() => validateAndSubmit()}
                      >
                        Search
                      </Button>
                    </FieldGroup>
                  </GridCol>
                </GridRow>
              </Form>
            ) : null}
            {userData.isAgent() || (!userData.isAgent() && runReport === true) || userData.isMockingAgent ? (
              <Tabs compact noLeftIndent>
                <Tab
                  labelVisual={
                    activities['billing'].tabCount == null
                      ? 'Billing'
                      : 'Billing (' + activities['billing'].tabCount + ')'
                  }
                >
                  <RecentActivitiesTable
                    dataCallBack={handleTableData}
                    getSortedItemsCallBack={getSortedItems}
                    sortedColumn={config.billing.sortedColumn}
                    columns={config.billing.columns}
                    activityDataType={'billing'}
                    onClickColumnKey={'PolicyNumber'}
                    exportData={exportRecentActivities}
                    isParentLoaded={isLoaded()}
                    agencyCode={usedAgencyCode}
                  ></RecentActivitiesTable>
                </Tab>
                <Tab
                  labelVisual={
                    activities['claims']?.tabCount == null ? 'Claims' : 'Claims (' + activities['claims'].tabCount + ')'
                  }
                >
                  <RecentActivitiesTable
                    dataCallBack={handleTableData}
                    getSortedItemsCallBack={getSortedItems}
                    sortedColumn={config.claims.sortedColumn}
                    columns={config.claims.columns}
                    activityDataType={'claims'}
                    link={sitecorelink}
                    exportData={exportRecentActivities}
                    isParentLoaded={isLoaded()}
                    agencyCode={usedAgencyCode}
                  ></RecentActivitiesTable>
                </Tab>
                <Tab
                  labelVisual={
                    activities['policy'].tabCount == null ? 'Policy' : 'Policy (' + activities['policy'].tabCount + ')'
                  }
                >
                  <RecentActivitiesTable
                    dataCallBack={handleTableData}
                    getSortedItemsCallBack={getSortedItems}
                    sortedColumn={config.policy.sortedColumn}
                    columns={config.policy.columns}
                    activityDataType={'policy'}
                    exportData={exportRecentActivities}
                    isParentLoaded={isLoaded()}
                    documentLink={policyLink}
                    agencyCode={usedAgencyCode}
                  ></RecentActivitiesTable>
                </Tab>
              </Tabs>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RecentActivities;
